import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from "react";
import { getSSEUrl } from "../api";

const SSEContext = createContext(null);

export function SSEProvider({ children }) {
  const [listeners] = useState(() => new Map());
  const esRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    if (esRef.current) esRef.current.close();

    const es = new EventSource(getSSEUrl());
    esRef.current = es;

    es.onerror = () => {
      es.close();
      reconnectTimer.current = setTimeout(connect, 3000);
    };

    // Register known events
    for (const event of ["lead:new", "lead:updated", "settings:updated", "presence"]) {
      es.addEventListener(event, (e) => {
        const data = JSON.parse(e.data);
        const cbs = listeners.get(event);
        if (cbs) cbs.forEach((cb) => cb(data));
      });
    }
  }, [listeners]);

  useEffect(() => {
    connect();
    return () => {
      if (esRef.current) esRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  const subscribe = useCallback(
    (event, cb) => {
      if (!listeners.has(event)) listeners.set(event, new Set());
      listeners.get(event).add(cb);
      return () => listeners.get(event).delete(cb);
    },
    [listeners]
  );

  return (
    <SSEContext.Provider value={{ subscribe }}>{children}</SSEContext.Provider>
  );
}

export function useSSE(event, callback) {
  const ctx = useContext(SSEContext);
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!ctx) return;
    return ctx.subscribe(event, (data) => cbRef.current(data));
  }, [ctx, event]);
}
